/**
 * Everything said when food, movement or water lands in the diary.
 *
 * The most-heard pools in the app, and therefore the ones that decide whether
 * she reads as a person or as a line generator. Written to PERSONA.md's spoken
 * register: uneven rhythm, particles, real questions, concrete nouns, and
 * roughly one line in three that is not a joke at all — just her, saying a
 * thing. Toasts — ≤ 120 chars at maximal context.
 */

import type { SassContext } from '@gramas/shared'
import {
  fmtKcal,
  overBy,
  shortName,
  shortNameLower,
  slotAcc,
  slotWord,
  snackCountLt,
  snackNo,
  voc,
} from '../fmt.js'

type Line = (ctx: SassContext) => string

export const LOGGING: Readonly<Record<string, ReadonlyArray<Line>>> = {
  'log_success.base': [
    () => 'Užrašiau.',
    () => 'Gerai. Įrašiau.',
    () => 'Aha. Mačiau, užrašiau.',
    () => 'Na va. Įrašyta.',
    () => 'Įrašiau. Tiek to.',
    () => 'Ką gi. Užrašyta.',
    () => 'Gerai jau, gerai. Įrašyta.',
    () => 'Užrašyta. Eik toliau.',
    () => 'Įrašiau ir nieko nesakysiu.',
    () => 'Va. Įrašyta.',
    () => 'Priimta. Tęsk.',
    () => 'Užrašiau. Ir tiek šiandien žinių.',
    () => 'Įrašyta. Šiandien nekomentuoju.',
    () => 'Aha, mačiau. Užrašyta.',
    () => 'Gerai. O toliau?',
    () => 'Užrašiau. Klausimų neturiu.',
    () => 'Įrašyta. Skanu bent buvo?',
    () => 'Užrašiau. Čia viskas, ar dar bus?',
    () => 'Įrašiau. Rytoj apie tai nekalbėsim.',
    () => 'Žinai ką, net nieko nesakysiu. Įrašyta.',
    () => 'Gerai. Užrašyta ir pamiršta. Beveik.',
    () => 'Įrašyta. Sėdžiu, žiūriu, rašau. Toks gyvenimas.',
    () => 'Tai va. Užrašiau.',
    () => 'Užrašiau. Nagi, dar vieno neįrašysi?',
    () => 'Įrašyta. Nemirktelėjau.',
    () => 'Mačiau viską. Užrašiau tik tai, ką reikia.',
    () => 'Įrašyta. Šaukštas nuleistas, byla uždaryta.',
    () => 'Užrašiau. Šaldytuvas irgi girdėjo, bet jis tyli.',
    () => 'Aha. Kažką valgei, kažką užrašiau. Simetrija.',
    () => 'Įrašiau. Truputį per greitai valgai, bet čia ne mano reikalas.',
    () => 'Užrašyta. Ir taip, aš matau, kelinta valanda.',
    () => 'Gerai. Skaičius nukeliavo ten, kur reikia.',
    () => 'Įrašiau. Svarstyklės rytoj pasakys, ką apie tai mano.',
    () => 'Užrašyta. Virtuvėje kažkas dar girgžda. Ne mano darbas.',
    () => 'Aha. Dabar tavo diena atrodo šiek tiek sąžiningiau.',
    () => 'Įrašyta. Tu valgai, aš rašau. Tokia sutartis, niekas jos nepasirašė.',
    () => 'Gerai jau. Skaičiuoju toliau.',
    () => 'Užrašiau. Ir net nepakėliau antakio. Šįkart.',
    () => 'Įrašyta. Vienu dalyku mažiau, kurio rytoj neprisiminsi.',
    () => 'Na gerai. Tai bent jau įrašei, o ne slėpei.',
    () => 'Užrašyta. Ar bent jau atsisėdai valgydamas?',
    () => 'Įrašiau. Kramtyti irgi reikia, ne tik ryti.',
    () => 'Aha. Užrašyta. Aš čia visą laiką, jei kas.',
    () => 'Gerai. Vienas įrašas, viena diena, vienas žmogus. Kaip visada.',
    () => 'Užrašiau. Orkaitė šiandien tyli, tai kalbėsiu aš.',
    () => 'Įrašyta. Ne mano kūnas, ne mano nuomonė. Skaičiai — mano.',
    () => 'Va taip. Diena po truputį įgauna formą.',
    () => 'Užrašyta. Beje, vandens gėrei? Klausiu iš mandagumo.',
    () => 'Įrašiau. Kartais tu mane nustebini. Šiandien — ne.',
    () => 'Gerai. Dar vienas kąsnis, dar viena eilutė. Mano darbas paprastas.',
    () => 'Aha, tas pats šaukštas, ta pati lėkštė. Užrašiau.',
    () => 'Užrašyta. Tu net nepastebėjai, kad aš čia. Priprasi.',
    () => 'Įrašyta. Slay, jei tau nuo to lengviau.',
    () => 'Gerai. Nieko baisaus nenutiko. Kol kas.',
    () => 'Užrašiau. Šiandien esu geros nuotaikos, tai tiek.',
    () => 'Įrašyta. Kartais tiesiog užrašau ir einu toliau.',
    () => 'Na. Įrašyta. Nesitikėk aplodismentų.',
    () => 'Užrašiau. Ir dar kartą pažiūrėjau į skaičių. Vis dar tas pats.',
    () => 'Įrašyta. Valgei stovėdamas prie šaldytuvo, ar aš čia fantazuoju?',
    () => 'Gerai jau. Rytoj apie tai net neužsiminsiu.',
    () => 'Užrašyta. Tarp mūsų kalbant, buvo ir blogesnių dienų.',
    () => 'Įrašiau. Tik nesakyk, kad tai paskutinis šiandien.',
    () => 'Aha. Įrašyta. Toliau tyla — kol kas nors vėl neatidarys šaldytuvo.',
    () => 'Užrašyta. Mirusiems lengviau — jiems nereikia nieko skaičiuoti.',
    () => 'Įrašyta. Aš mačiau ir baisesnių vakarienių. Prieš šimtą metų.',
    () => 'Gerai. Vienas įrašas nieko nesprendžia. Bet aš juos renku.',
    () => 'Užrašiau. Namuose tylu, tik tu ir aš. Ir šaldytuvas.',
    () => 'Įrašyta. Kažkas turi tai matyti. Man teko.',
    () => 'Na gerai, branguti. Įrašiau.',
    () => 'Užrašyta. Tavo skonis manęs nestebina jau kelis mėnesius.',
    () => 'Įrašiau. Ir jei klausi — ne, aš nemiegu.',
    () => 'Aha. Tai buvo greita. Net nespėjau nusistebėti.',
    () => 'Užrašyta. Tavo lėkštė tuščia, mano lentelė — ne.',
    () => 'Įrašyta. Čia tyliau nei kapinėse, o aš žinau, apie ką kalbu.',
    () => 'Gerai. Skaičiuosiu toliau, tu valgyk toliau. Sutarėm.',
    () => 'Užrašiau. Praėjo šimtas metų, o žmonės vis dar valgo naktį.',
    () => 'Įrašyta. Kūnas užmirš, aš — ne. Tokia specialybė.',
    () => 'Na va, dar viena eilutė. Jos gražiai kaupiasi.',
    () => 'Užrašyta. Šįkart net nesiginčysiu.',
    () => 'Įrašiau. Šaldytuvas atsiduso. Girdėjau.',
    () => 'Gerai. Diena tęsiasi, aš irgi.',
    () => 'Užrašiau. Iconic, kaip sakoma. Nors kas taip sako.',
    () => 'Įrašyta. Vieną dieną kažkas skaitys šitą sąrašą. Tikiuosi, ne tu.',
    () => 'Aha. Vienas įrašas — ir tavo diena vėl turi prasmę. Beveik.',
    () => 'Užrašyta. Nagi, prisipažink: skanu buvo.',
    () => 'Įrašiau. Rytas buvo geresnis, bet ir šitas tiks.',
    () => 'Gerai. Nieko dramatiško. Man patinka.',
    () => 'Užrašyta. Tik nežiūrėk į mane taip. Aš tik rašau.',
  ],
  // Naming the thing is the cheapest situatedness there is — a line that
  // could have been written before you ate is not a reaction to eating.
  'log_success.named': [
    (c) => `${shortName(c)}. Na žinoma.`,
    (c) => `Aha, ${shortNameLower(c)}. Užrašiau.`,
    (c) => `${shortName(c)}. Gerai, tebūnie.`,
    (c) => `Va, ${shortNameLower(c)}. Klasika.`,
    (c) => `${shortName(c)}? Gerai. Įrašiau.`,
    (c) => `${shortName(c)}. Tavo skonis nesikeičia.`,
    (c) => `Ką gi. ${shortName(c)}. Užrašyta.`,
    (c) => `${shortName(c)}. Net neklausiu kodėl.`,
    (c) => `Įrašiau: ${shortNameLower(c)}. Skanaus, jei dar nevėlu linkėti.`,
    (c) => `${shortName(c)}. Šito aš tikėjausi.`,
    (c) => `${shortName(c)}. Šito — ne.`,
    (c) => `Aha, vėl ${shortNameLower(c)}. Nieko nesakau.`,
    (c) => `${shortName(c)}. Užrašiau, nors ir be entuziazmo.`,
    (c) => `Gerai jau. ${shortName(c)}. Byloje užfiksuota.`,
    (c) => `${shortName(c)}. Drąsu. Įrašyta.`,
    (c) => `${shortName(c)}, taip? Tebūnie.`,
    (c) => `Na va. ${shortName(c)}. Diena turi kryptį.`,
    (c) => `${shortName(c)}. Šitas pasirinkimas kai ką apie tave sako.`,
    (c) => `Įrašyta: ${shortNameLower(c)}. Komentarų nebus.`,
    (c) => `${shortName(c)}. Tiek to, būna ir blogiau.`,
    (c) => `Aha. ${shortName(c)}. Šaldytuvas jau žinojo.`,
    (c) => `${shortName(c)}. Gerai, užrašiau. Tik tyliai.`,
  ],
  // A dinner is three writes in twenty seconds. Three lines pretending the
  // other two didn't happen is the clearest tell that nobody is listening.
  'log_success.burst2': [
    () => 'Ir dar vienas. Gerai, rašau.',
    () => 'Aha, tęsiam.',
    () => 'Dar? Gerai.',
    () => 'Antras. Užrašiau.',
    () => 'Na štai, prasidėjo.',
    () => 'Ir tai. Gerai.',
    () => 'Dar vienas. Neskubu, rašau.',
    () => 'Aha. Vadinasi, ne viskas buvo.',
    () => 'Antra eilutė per minutę. Įdomu.',
    () => 'Gerai, dar vienas. Klausau toliau.',
  ],
  'log_success.burst3': [
    () => 'Visa vakarienė iš karto. Suprantu.',
    () => 'Gerai, gerai. Aš spėju.',
    () => 'Trečias. Tu rimtai nusiteikęs.',
    () => 'Nagi. Ar dar kas nors?',
    () => 'Rašau, rašau. Nenustok.',
    () => 'Trečia eilutė. Aš jau pavargau, o aš net negyva.',
    () => 'Va, dabar suprantu, kas ten vyko virtuvėje.',
  ],
  'log_success.huge_portion': [
    (c) => `${fmtKcal(c.entryKcal)} kcal vienu ypu. Nagi.`,
    (c) => `Aha. ${fmtKcal(c.entryKcal)} kcal. Vienu prisėdimu.`,
    (c) => `${fmtKcal(c.entryKcal)} kcal. Tai buvo ne užkandis.`,
    (c) => `Įrašiau ${fmtKcal(c.entryKcal)} kcal. Alkis buvo tikras, matau.`,
    (c) => `${fmtKcal(c.entryKcal)} kcal. Na po velnių.`,
    (c) => `${fmtKcal(c.entryKcal)} kcal. Sėdžiu tyliai ir rašau.`,
    (c) => `${fmtKcal(c.entryKcal)} kcal viena porcija. Kada paskutinį kartą valgei?`,
    (c) => `${fmtKcal(c.entryKcal)} kcal. Lėkštė turbūt girgžda.`,
    (c) => `Užrašyta: ${fmtKcal(c.entryKcal)} kcal. Gerai. Kvėpuok.`,
    (c) => `${fmtKcal(c.entryKcal)} kcal. RIP likučiui.`,
    (c) => `${fmtKcal(c.entryKcal)} kcal. Aš mačiau laidotuvių pietus kuklesnius.`,
    (c) => `${fmtKcal(c.entryKcal)} kcal. Gerai jau, buvo skanu. Suprantu.`,
  ],
  'log_success.nth_snack': [
    (c) => `Užkandis Nr. ${snackNo(c)}. Aš skaičiuoju, tu — ne.`,
    (c) => `Užkandis Nr. ${snackNo(c)}. Nagi, pavadink tai vakariene.`,
    () => 'Dar užkandis. Žodis „užkandis“ dirba viršvalandžius.',
    (c) => `Jau ${snackCountLt(c)}. Čia jau ne atsitiktinumas.`,
    (c) => `Nr. ${snackNo(c)}. Gerai. Nieko nesakau.`,
    (c) => `Užkandis Nr. ${snackNo(c)}. Alkis ar nuobodulys? Klausiu rimtai.`,
    (c) => `Jau ${snackCountLt(c)} šiandien. Ritmas geras, biudžetas — ne.`,
    (c) => `Dar vienas — jau ${snackNo(c)}. Aš neteisiu, aš tik matau.`,
    (c) => `Užkandis Nr. ${snackNo(c)}. Gilus įkvėpimas. Ir įrašyta.`,
    (c) => `Nr. ${snackNo(c)}. Tu ir šaldytuvas šiandien labai artimi.`,
    (c) => `${snackCountLt(c)}. Sausainiukai turi savo skaitiklį, žinojai?`,
    (c) => `Užkandis Nr. ${snackNo(c)}. Va čia jau įdomu darosi.`,
  ],
  'log_success.midnight': [
    () => 'Naktinis įrašas. Šaldytuvas išdavė, aš tik patvirtinu.',
    () => 'Aha. Trečia nakties, o mes vis dar dirbam.',
    () => 'Naktį valgei. Gerai. Nieko naujo po saule, kurios dabar nėra.',
    () => 'Užrašiau. Naktį viskas skanesnė, aš žinau.',
    () => 'Tamsu, tylu, ir tu prie šaldytuvo. Klasika.',
    () => 'Naktinis reidas užfiksuotas. Rytas turės klausimų.',
    () => 'Įrašiau. Eik miegoti, branguti.',
    () => 'Ką gi. Naktinė pamaina. Mano mėgstamiausia.',
    () => 'Užrašyta. Naktį drąsa pigesnė, kalorijos — tos pačios.',
    () => 'Aha, vidurnaktis. Bent jau įrašei, o ne slėpei.',
    () => 'Naktis. Tu valgai, aš rašau, niekas nemiega. Idilė.',
    () => 'Įrašyta. Labanakt. Rimtai — labanakt.',
  ],
  'log_success.tipped_over': [
    (c) => `Va, šitas ir perpildė. Viršyta ${overBy(c)} kcal. Rytoj vėl ${fmtKcal(c.kcalTarget)}.`,
    (c) => `Ir štai. Viršyta ${overBy(c)} kcal. Rytoj — švarus lapas.`,
    (c) => `Šitas įrašas pramušė lubas: ${overBy(c)} kcal per daug.`,
    (c) => `Aha. Viršyta ${overBy(c)} kcal. Būtent šitas, jei tau įdomu.`,
    (c) => `Na po velnių. ${overBy(c)} kcal virš tikslo.`,
    (c) => `Biudžetas baigėsi būtent čia: ${overBy(c)} kcal per daug.`,
    (c) => `Viršyta ${overBy(c)} kcal. Savaitės vidurkis dar gali tave užtarti.`,
    (c) => `Perlipta. ${overBy(c)} kcal virš ribos. Rytoj ji sugrįš, ribos tokios.`,
    (c) => `Gerai. Šitas buvo per daug — ${overBy(c)} kcal. Ir aš tai užrašiau.`,
    (c) => `${overBy(c)} kcal viršaus. Tiek to. Vakaras dar gali baigtis oriai.`,
    (c) => `Ir tada atėjo šitas. Viršyta ${overBy(c)} kcal. Įsimintina.`,
    (c) => `Riba kirsta — ${overBy(c)} kcal. Rytoj pradedam iš naujo, be dramos.`,
  ],
  'entry_edited.base': [
    () => 'Pataisyta. Tiesa pas tave lanksti.',
    () => 'Gerai. Perrašiau.',
    () => 'Aha. Vadinasi, pirma versija buvo juodraštis.',
    () => 'Atnaujinta. Skaičiai keičiasi, aš — ne.',
    () => 'Pataisiau. Antakio nekeliu. Beveik.',
    () => 'Gerai jau, tebūnie tavo versija.',
    () => 'Perrašyta. Praeitis dabar atrodo geriau. Patogu.',
    () => 'Pataisyta. Abi versijas mačiau, jei kas.',
    () => 'Na va. Nauja tiesa įsigaliojo.',
    () => 'Atnaujinta. Tikslumas — pavėluotas, bet vis dėlto tikslumas.',
    () => 'Pataisiau. Antras bandymas visada nuoširdesnis.',
    () => 'Gerai. Skaičius pasikeitė, diena — ne.',
    () => 'Perrašyta. Tu tiksliniesi, aš rašau. Puiki komanda.',
    () => 'Pataisyta. Ir taip, aš prisimenu, kas buvo prieš tai.',
  ],
  'entry_moved.base': [
    (c) => `Gerai. Dabar tai ${slotWord(c)}.`,
    (c) => `Perkėliau į ${slotAcc(c)}. Laikas pas tave sutartinis dalykas.`,
    (c) => `Aha. ${slotWord(c)}, taip ir užrašysim.`,
    (c) => `Perkelta į ${slotAcc(c)}. Alibi sutvarkytas.`,
    () => 'Perkėliau. Valgis rado savo vietą gyvenime.',
    (c) => `Į ${slotAcc(c)}. Chronologija išgelbėta.`,
    (c) => `Gerai jau. ${slotWord(c)} tebūnie.`,
    () => 'Perkelta. Skaičiams vis vien, bet tvarka nuramina. Mane.',
    () => 'Na va. Dienos siužetas tapo logiškesnis.',
    (c) => `Į ${slotAcc(c)} — ir taškas.`,
    () => 'Perkėliau. Persikraustymas be nuostolių.',
    (c) => `${slotWord(c)}? Gerai. Bet mudu abu žinom, kada tai buvo.`,
  ],
  'meal_repeated.base': [
    (c) => `Vakar veikė, veiks ir šiandien. Perkėliau ${c.count ?? 0}.`,
    () => 'Kartojam vakarykštę programą. Man tinka.',
    (c) => `Perkelta ${c.count ?? 0} įr. iš vakar. Stabilumas irgi talentas.`,
    () => 'Tas pats, kas vakar. Net nemirktelėjau.',
    () => 'Aha. Vakardiena grįžo bisui.',
    (c) => `Įrašiau ${c.count ?? 0}. Meniu siauras, užtat pažįstamas.`,
    () => 'Gerai. Kūryba šiandien ilsisi.',
    (c) => `Perkėliau ${c.count ?? 0}. Rutina — pastovumo aukščiausia forma.`,
    (c) => `Vakarykščiai hitai vėl grojaraštyje. ${c.count ?? 0} įr.`,
    () => 'Pakartota. Klasika nenuvilia. Beveik niekada.',
    () => 'Na va. Vakar ir šiandien susiliejo į vieną dieną.',
    () => 'Perkėliau. Tu net nepagalvojai, ką valgysi, tiesa?',
  ],
  'burn_logged.base': [
    () => 'Judėjai. Užrašiau. Sofa tavęs pasiilgs.',
    () => 'Gerai. Prakaitas irgi valiuta.',
    () => 'Aha, treniruotė. Skausmas praeis, skaičius liks.',
    () => 'Užrašyta. Šiandien sofa laimėjo ne viską.',
    () => 'Įrašiau. Biudžetas kilstelėjo, nedaug, bet kilstelėjo.',
    () => 'Na va. Raumenys pasirašė protokolą.',
    () => 'Judėjimas užskaitytas. Vienintelis legalus būdas prisidurti kalorijų.',
    () => 'Gerai jau. Deginta. Ugnies nemačiau, bet patikėsiu.',
    () => 'Užrašiau. Endorfinai — premija, skaičiai — alga.',
    () => 'Aha. Tai va kur tu buvai.',
    () => 'Įrašyta. Kūnas dirbo, aš skaičiavau. Kaip visada.',
    () => 'Gerai. Rytoj skaudės, bet skaičius jau mano lentelėje.',
  ],
  'water_milestone.base': [
    () => 'Du litrai. Bent kažkas šiandien pagal planą.',
    () => 'Aha, 2 l. Inkstai siunčia padėką.',
    () => 'Du litrai vandens. Nulis kcal, nulis paskalų. Gerbiu.',
    () => 'Na va. Vanduo — vienintelis besąlygiškas sąjungininkas.',
    () => 'Gerai. 2 l. Tyliausia šios dienos pergalė.',
    () => 'Du litrai. Čiaupas didžiuojasi labiau nei aš.',
    () => 'Užrašiau: 2 l. Vienintelis skaičius šiandien be išlygų.',
    () => 'Aha. Vandens norma įvykdyta. Liko tik visa kita.',
    () => 'Du litrai. Šito net pakomentuoti negaliu blogai.',
    () => '2 l. Kūnas daugiausia iš vandens, tavasis šiandien pasitempė.',
    () => 'Gerai jau. Hidratacija — bazinis self-care. Užskaityta.',
    () => 'Du litrai. Toliau — laisvas plaukiojimas.',
    () => 'Na. Vanduo sutvarkytas. Dabar apie maistą pakalbėkim.',
    () => '2 l. Va čia jau matosi žmogus, kuris skaito etiketes.',
    (c) => `Du litrai${voc(c)}. Šiandien tavo pusėje bent vanduo.`,
  ],
}

export const LOGGING_META: Readonly<Record<string, number>> = {
  // Split across the three ordinary log pools: most logs draw from `base`,
  // about a third of the ones that can name their food draw from `named`.
  'log_success.base': 55,
  'log_success.named': 20,
  'log_success.burst2': 8,
  'log_success.burst3': 5,
  'log_success.huge_portion': 8,
  'log_success.nth_snack': 8,
  'log_success.midnight': 4,
  'log_success.tipped_over': 7,
  'entry_edited.base': 8,
  'entry_moved.base': 4,
  'meal_repeated.base': 7,
  'burn_logged.base': 7,
  'water_milestone.base': 14,
}
